import { useState } from 'react';
import { clsx } from '../../components/clsx';
import { IconButton, TextInput } from '../../components/ui';
import {
  MAX_WALLET_NAME,
  parseBtcAmount,
  type StackTotals,
  type Wallet,
} from '../../lib/bitcoin/stack';
import { formatBtc, formatCompact, formatUsd } from '../../lib/bitcoin/units';

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={clsx(
        'h-3 w-3 shrink-0 text-ink-subtle transition-transform',
        open && 'rotate-180',
      )}
      aria-hidden
    >
      <path
        d="M3 4.5 6 7.5 9 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Summary bar — lives in the Card footer, always visible              */
/* ------------------------------------------------------------------ */

export interface StackSummaryBarProps {
  readonly totals: StackTotals;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly panelId: string;
}

/**
 * One line, ~22px of ink, and the only affordance that opens the editor.
 * The editor deliberately opens INSIDE the card (replacing the chart) rather
 * than in a modal or a settings route: spec.md requires "user inputs BTC
 * amount per named wallet location", and its Non-Goals forbid accounts,
 * backends and mobile layouts — not in-card interaction. A card that grows to
 * fit an editor would break the no-scroll criterion, so it swaps instead.
 */
export function StackSummaryBar({ totals, open, onToggle, panelId }: StackSummaryBarProps) {
  const empty = totals.totalSats === 0;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={panelId}
      className={clsx(
        'flex w-full items-center gap-2 rounded text-left transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
      )}
    >
      <span className="shrink-0 text-[8.5px] font-semibold uppercase tracking-[0.1em] text-ink-subtle">
        Stack
      </span>
      {empty ? (
        <span className="truncate text-[11px] text-ink-subtle">
          {open ? 'Name a wallet and enter its BTC' : 'Add your wallets'}
        </span>
      ) : (
        <>
          <span className="shrink-0 font-mono text-[12px] font-semibold text-ink">
            {`₿ ${formatBtc(totals.totalBtc)}`}
          </span>
          <span className="truncate text-[9.5px] text-ink-subtle">
            {`${formatCompact(totals.totalSats)} sats`}
          </span>
          <span className="ml-auto shrink-0 font-mono text-[12px] font-semibold text-ink">
            {totals.totalUsd === null ? '—' : formatUsd(totals.totalUsd)}
          </span>
        </>
      )}
      <span className={clsx('shrink-0', empty && 'ml-auto')}>
        <Chevron open={open} />
      </span>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Editor — replaces the chart while open                              */
/* ------------------------------------------------------------------ */

/**
 * The amount field holds its own draft string. A controlled input bound
 * straight to the parsed number cannot be typed into: "0." round-trips to
 * "0" and the decimal point is eaten on every keystroke.
 */
function WalletRow({
  wallet,
  price,
  onRename,
  onAmount,
  onRemove,
}: {
  wallet: Wallet;
  price: number | null;
  onRename: (name: string) => void;
  onAmount: (btc: number) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(() => (wallet.btc === 0 ? '' : formatBtc(wallet.btc)));
  const invalid = draft.trim() !== '' && parseBtcAmount(draft) === null;
  const usd = price === null ? null : wallet.btc * price;

  return (
    <div className="flex items-center gap-1.5">
      <TextInput
        value={wallet.name}
        onChange={(e) => onRename(e.target.value)}
        maxLength={MAX_WALLET_NAME}
        placeholder="Wallet name"
        aria-label="Wallet name"
        className="min-w-0 flex-1 !py-1 !text-[12px]"
      />
      <TextInput
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const parsed = parseBtcAmount(e.target.value);
          if (parsed !== null) onAmount(parsed);
        }}
        inputMode="decimal"
        placeholder="0.00"
        aria-label={`BTC held in ${wallet.name}`}
        aria-invalid={invalid}
        title={usd === null ? undefined : formatUsd(usd)}
        className={clsx(
          'w-[84px] shrink-0 text-right font-mono !py-1 !text-[12px]',
          invalid && 'border-negative',
        )}
      />
      <IconButton label={`Remove ${wallet.name}`} onClick={onRemove} className="h-6 w-5">
        <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden>
          <path
            d="M3 3l6 6M9 3l-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </IconButton>
    </div>
  );
}

export interface StackEditorProps {
  readonly wallets: readonly Wallet[];
  readonly price: number | null;
  readonly onRename: (id: string, name: string) => void;
  readonly onAmount: (id: string, btc: number) => void;
  readonly onRemove: (id: string) => void;
  readonly onAdd: () => void;
  readonly panelId: string;
}

export function StackEditor({
  wallets,
  price,
  onRename,
  onAmount,
  onRemove,
  onAdd,
  panelId,
}: StackEditorProps) {
  return (
    <div
      id={panelId}
      // Scrolls internally so a twelve-wallet stack cannot push the card past
      // the viewport. The Card body itself still clips.
      className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-0.5"
    >
      {wallets.map((w) => (
        <WalletRow
          key={w.id}
          wallet={w}
          price={price}
          onRename={(name) => onRename(w.id, name)}
          onAmount={(btc) => onAmount(w.id, btc)}
          onRemove={() => onRemove(w.id)}
        />
      ))}
      <button
        type="button"
        onClick={onAdd}
        className={clsx(
          'shrink-0 rounded-lg border border-dashed border-line py-1 text-[11px] font-medium',
          'text-ink-subtle transition-colors hover:bg-panel-raised hover:text-ink',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        )}
      >
        + Add wallet
      </button>
    </div>
  );
}
